import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";

import { db } from "../../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  limit,
} from "firebase/firestore";
import dayjs from "dayjs";
import {
  writeBatch,
  addDoc,
  arrayUnion,
  serverTimestamp,
  orderBy,
} from "firebase/firestore";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import Badge from "@mui/material/Badge";

import {
  fetcherOrdersUserdriver,
  fetcherUserdriver,
  fetcherRoutes,
} from "../../../utils/FectcherData/FectcherUser";

import Rendimiento from "./Components/Rendimiento";
import Historial from "./Components/Historial";
import Trayectos from "./Components/Trayectos";
import Pagos from "./Components/Pagos";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";

// --------------------------------------------------
// Helpers
// --------------------------------------------------
const normalizeEmail = (v) => (v || "").trim().toLowerCase();

const safeString = (v) => (v === null || v === undefined ? "" : String(v));

const toListKV = (obj, prefix = "") => {
  if (!obj || typeof obj !== "object") return [];
  const out = [];
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out.push(...toListKV(v, key));
    } else {
      out.push({ key, value: v });
    }
  }
  return out;
};

// --------------------------------------------------
// Helpers docs
// --------------------------------------------------
const guessFileNameFromUrl = (url, fallback = "documento") => {
  try {
    const u = new URL(url);
    const path = decodeURIComponent(u.pathname || "");
    const last = path.split("/").filter(Boolean).pop() || fallback;
    // limpia cosas raras
    const clean = last.replace(/[^\w.\-() ]+/g, "_");
    // asegura extensión mínima si no trae
    if (!/\.[a-z0-9]{2,5}$/i.test(clean)) return `${clean}.file`;
    return clean;
  } catch {
    return `${fallback}.file`;
  }
};

const collectUrlDocsFromKV = (obj, prefix = "") => {
  const rows = toListKV(obj, prefix);
  return rows
    .filter((kv) => isUrl(kv.value))
    .map((kv) => ({
      key: kv.key,
      label: kv.key,
      url: kv.value,
      section: "Otros",
    }));
};

const pushDoc = (arr, { section, label, url, meta }) => {
  if (!url || !isUrl(url)) return;
  arr.push({
    id: `${section}:${label}:${url}`,
    section,
    label,
    url,
    meta: meta || null,
  });
};

const dedupeDocs = (docs) => {
  const seen = new Set();
  const out = [];
  for (const d of docs) {
    const k = `${d.section}|${d.label}|${d.url}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(d);
  }
  return out;
};

const isUrl = (v) => typeof v === "string" && /^https?:\/\//i.test(v);

const labelMap = {
  "vehicleInfo.vehicle.typeLabel": "Tipo de vehículo",
  "vehicleInfo.vehicle.ownership": "Propiedad",
  "vehicleInfo.vehicle.type": "Tipo (ID)",
  "vehicleInfo.vehicle.require": "Requiere validación",
  "vehicleInfo.vehicle.color": "Color",
  "vehicleInfo.updatedItem": "Actualizado (perfil)",
  "vehiculoDoc.id": "ID Vehículo",
  "vehiculoDoc.name": "Nombre (catálogo)",
};

const formatOwnership = (v) => {
  if (v === "own") return "Propio";
  if (v === "third") return "Tercero";
  return v ? String(v) : "—";
};

const formatBool = (v) => {
  if (typeof v !== "boolean") return "—";
  return v ? "Sí" : "No";
};

const formatTimestampSeconds = (seconds) => {
  if (!seconds) return "—";
  const d = new Date(seconds * 1000);
  return d.toLocaleString("es-CL");
};

const isNonEmpty = (v) =>
  v !== null && v !== undefined && String(v).trim().length > 0;

const pushIfMissing = (arr, ok, label) => {
  if (!ok) arr.push(label);
};

const truthy = (v) => v === true || v === "true" || v === 1;

const pickObj = (...candidates) =>
  candidates.find((x) => x && typeof x === "object") || {};

const pickStr = (...candidates) =>
  candidates.find((x) => typeof x === "string" && x.trim().length) || "";

const hasAnyUrl = (obj, keys) => keys.some((k) => isUrl(obj?.[k]));

const missingPush = (arr, label, condOk) => {
  if (!condOk) arr.push(label);
};

export const computeSignatures = (d) => {
  const consents = pickObj(d.consentsInfo, d.consents, d.consentimientos);
  const docs = pickObj(d.documents, d.docs, d.documentos);
  const flags = pickObj(d.flags);

  const contractSigned =
    truthy(consents.contractSigned) ||
    truthy(flags.signatureDocument) ||
    hasAnyUrl(docs, [
      "signatureDocumentPdfUrl",
      "contractPdfUrl",
      "contratoPdfUrl",
    ]);

  const paymentSigned =
    truthy(consents.paymentSigned) ||
    truthy(flags.signatureMandate) ||
    hasAnyUrl(docs, ["mandatePdfUrl", "paymentMandatePdfUrl", "mandatoPdfUrl"]);

  const protocolSigned =
    truthy(consents.protocolSigned) ||
    truthy(flags.signatureProtocol) ||
    hasAnyUrl(docs, [
      "signatureProtocolPdfUrl",
      "protocolPdfUrl",
      "protocoloPdfUrl",
    ]);

  return { contractSigned, paymentSigned, protocolSigned };
};

export const computeOnboardingHealth = (dRaw) => {
  const d = dRaw || {};

  // -----------------------------
  // Sources (según tu objeto real)
  // -----------------------------
  const basicInfo = pickObj(d.basicInfo);
  const contactPrefs = pickObj(d.contactPreferencesInfo);

  const driverLicenseInfo = pickObj(d.driverLicenseInfo);
  const driverLicense = pickObj(driverLicenseInfo.driverLicense);

  const vehicleInfo = pickObj(d.vehicleInfo);
  const vehicle = pickObj(vehicleInfo.vehicle);

  const paymentWrap = pickObj(d.paymentInfo);
  const paymentInfo = pickObj(paymentWrap.paymentInfo);

  const billingWrap = pickObj(d.billingInfo);
  const billingInfo = pickObj(billingWrap.billingInfo);

  const complianceWrap = pickObj(d.compliance);
  const compliance = pickObj(complianceWrap.compliance);

  const consents = pickObj(d.consentsInfo, d.consents, d.consentimientos);
  const docs = pickObj(d.documents, d.docs, d.documentos);

  // -----------------------------
  // Section checks
  // -----------------------------
  const sections = {};

  // 1) Perfil + avatar
  {
    const m = [];
    const avatarUrl = pickStr(
      d.avatar,
      contactPrefs.avatarUrl,
      docs.avatarUrl,
      d.photoUrl
    );

    missingPush(m, "Foto de perfil (avatar)", isUrl(avatarUrl));
    missingPush(m, "Nombre", !!pickStr(d.name));
    missingPush(m, "Email", !!pickStr(d.email));
    missingPush(m, "Teléfono", !!pickStr(d.phone));

    sections.profile = { ok: m.length === 0, missing: m };
  }

  // 2) Identidad (DNI)
  {
    const m = [];
    const dniFront = pickStr(
      basicInfo.dniFrontUrl,
      d.dniFile,
      docs.dniFrontUrl
    );
    const dniBack = pickStr(basicInfo.dniBackUrl, docs.dniBackUrl);

    missingPush(m, "DNI frente", isUrl(dniFront));
    missingPush(m, "DNI reverso", isUrl(dniBack));
    missingPush(m, "RUT / DNI (campo dni)", !!pickStr(d.dni));

    sections.identity = { ok: m.length === 0, missing: m };
  }

  // 3) Licencia (solo si require === true)
  {
    const m = [];
    const requireLicense = truthy(driverLicense.require);

    if (requireLicense) {
      const licFront = pickStr(
        driverLicenseInfo.licenseFrontUrl,
        d.driverLicence
      );
      const licBack = pickStr(driverLicenseInfo.licenseBackUrl);

      missingPush(m, "Licencia frente", isUrl(licFront));
      missingPush(m, "Licencia reverso", isUrl(licBack));
      missingPush(m, "Clase licencia", !!pickStr(driverLicense.licenseClass));
      missingPush(m, "Número licencia", !!pickStr(driverLicense.licenseNumber));
      missingPush(
        m,
        "Vencimiento licencia (expiresAt)",
        !!pickStr(driverLicense.expiresAt)
      );
    }

    sections.license = {
      ok: m.length === 0,
      missing: m,
      note: requireLicense ? undefined : "No requerida",
    };
  }

  // 4) Compliance / certificado
  {
    const m = [];
    const declarationOk = truthy(compliance.declarationOk);

    // en tu objeto: complianceWrap.certFileUrl y antecedentesFile
    const certUrl = pickStr(complianceWrap.certFileUrl, d.antecedentesFile);

    missingPush(m, "Declaración compliance (declarationOk)", declarationOk);
    missingPush(m, "Certificado antecedentes (PDF)", isUrl(certUrl));
    missingPush(
      m,
      "Fecha certificado (certDate)",
      !!pickStr(compliance.certDate)
    );

    sections.compliance = { ok: m.length === 0, missing: m };
  }

  // 5) Vehículo (asignación + typeLabel)
  {
    const m = [];
    missingPush(m, "Vehículo asignado (vehicleID)", !!pickStr(d.vehicleID));
    missingPush(
      m,
      "Tipo vehículo (vehicleInfo.vehicle.typeLabel)",
      !!pickStr(vehicle.typeLabel)
    );
    sections.vehicle = { ok: m.length === 0, missing: m };
  }

  // 6) Banco / pagos
  {
    const m = [];
    missingPush(m, "Banco", !!pickStr(paymentInfo.bank));
    missingPush(m, "Tipo de cuenta", !!pickStr(paymentInfo.accountType));
    missingPush(m, "N° de cuenta", !!pickStr(paymentInfo.accountNumber));
    missingPush(m, "Titular (RUT)", !!pickStr(paymentInfo.holderRut));
    missingPush(m, "Titular (Nombre)", !!pickStr(paymentInfo.holderName));
    sections.payment = { ok: m.length === 0, missing: m };
  }

  // 7) Facturación (si scheme=factura exigir company fields)
  {
    const m = [];
    const scheme = pickStr(billingInfo.scheme); // boleta | factura

    missingPush(m, "Esquema tributario (scheme)", !!scheme);

    if (scheme === "factura") {
      missingPush(m, "Razón social", !!pickStr(billingInfo.companyName));
      missingPush(m, "RUT empresa", !!pickStr(billingInfo.companyRut));
      missingPush(m, "Giro", !!pickStr(billingInfo.giro));
    }

    sections.billing = { ok: m.length === 0, missing: m };
  }

  // 8) Consentimientos (NO excluir términos / privacidad)
  {
    const m = [];
    const termsAccepted =
      truthy(consents.terms) ||
      truthy(consents.termsAccepted) ||
      truthy(consents.termsOk);

    const privacyAccepted =
      truthy(consents.privacy) ||
      truthy(consents.privacyAccepted) ||
      truthy(consents.privacyOk);

    missingPush(m, "Aceptó términos", termsAccepted);
    missingPush(m, "Aceptó privacidad", privacyAccepted);

    sections.consents = { ok: m.length === 0, missing: m };
  }

  // 9) Firmas (contrato/mandato/protocolo)
  {
    const m = [];
    const sig = computeSignatures(d);

    missingPush(m, "Contrato firmado", sig.contractSigned);
    missingPush(m, "Mandato pagos firmado", sig.paymentSigned);
    missingPush(m, "Protocolo firmado", sig.protocolSigned);

    sections.signatures = { ok: m.length === 0, missing: m, signatures: sig };
  }

  // -----------------------------
  // Aggregate (global)
  // -----------------------------
  const missing = Object.entries(sections).flatMap(([sectionKey, s]) =>
    (s.missing || []).map((x) => `${sectionKey}: ${x}`)
  );

  const ok = missing.length === 0;
  const severity = ok ? "success" : "warning";

  return {
    ok,
    severity,
    title: ok
      ? "Onboarding completo ✅"
      : `Onboarding incompleto: faltan ${missing.length} item(s)`,
    missing,
    sections,
  };
};

// --------------------------------------------------
// Component
// --------------------------------------------------
const DriversDetails = () => {
  const navigate = useNavigate();
  const { id: driverId } = useParams();

  // Data base (fetchers existentes)
  const [user, setUserData] = useState(null);
  const [driverOperationData, setDriverOperationData] = useState(null);
  const [dailyTrips, setDailyTrips] = useState(null);

  // Extra data firestore (config / docs)
  const [driverDoc, setDriverDoc] = useState(null); // drivers/{id}
  const [companyLinkDoc, setCompanyLinkDoc] = useState(null); // companies/{companyId}/companydrivers/{email}
  const [stores, setStores] = useState([]);
  const [shifts, setShifts] = useState([]);

  const [loadingExtra, setLoadingExtra] = useState(false);
  const [errExtra, setErrExtra] = useState(null);

  // UI
  const [tab, setTab] = useState("overview"); // overview | operation | access | geo | shifts | vehicle | docs | raw
  const [opModule, setOpModule] = useState("HistorialDriver"); // Historial/Rendimiento/Trayectos/Pagos

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // Editable form state (config conductor)
  const [form, setForm] = useState({
    active: true,
    isblocked: false,
    requireTracking: false,
    hiddenListMenus: false,
    webRidersEnabled: true, // nombre recomendado
    workStoreId: "",
    storeIds: [],
    shiftIds: [],
    vehicleID: "",
    plate: "", // patente
  });

  const [driverShifts, setDriverShifts] = useState([]); // turnos del conductor
  const [selectedDay, setSelectedDay] = useState(dayjs()); // día seleccionado en calendario
  const [vehicleDoc, setVehicleDoc] = useState(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [newShift, setNewShift] = useState({
    storeId: "",
    date: dayjs().format("YYYY-MM-DD"),
    startTime: "10:00",
    endTime: "19:00",
    status: "SCHEDULED",
  });
  // --------------------------------------------------
  // Load base data
  // --------------------------------------------------
  const buscarConductor = useCallback(async () => {
    if (!driverId) return;

    const userData = await fetcherUserdriver(driverId);
    const orders = await fetcherOrdersUserdriver(driverId);
    const routes = await fetcherRoutes(driverId);

    setDriverOperationData(orders || []);
    setUserData(userData || null);
    setDailyTrips(routes || []);
  }, [driverId]);

  useEffect(() => {
    buscarConductor();
  }, [buscarConductor]);

  useEffect(() => {
    if (!driverId && !selectedDay) return;
    loadDriverShifts();
  }, [selectedDay]);

  useEffect(() => {
    let alive = true;

    (async () => {
      const vehicleId = form?.vehicleID;

      if (!vehicleId) {
        if (alive) setVehicleDoc(null);
        return;
      }

      try {
        setLoadingVehicle(true);

        // 👇 cambia "vehiculos" si tu colección se llama distinto
        const vRef = doc(collection(db, "vehicles"), vehicleId);
        const vSnap = await getDoc(vRef);

        if (!alive) return;

        if (vSnap.exists()) {
          setVehicleDoc({ id: vSnap.id, ...vSnap.data() });
        } else {
          setVehicleDoc({ id: vehicleId, _missing: true });
        }
      } catch (e) {
        console.error("Error cargando vehiculo:", e);
        if (alive) setVehicleDoc({ id: form?.vehicleID, _error: true });
      } finally {
        if (alive) setLoadingVehicle(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [form?.vehicleID]);

  // ---- Estado para vehículo (doc externo) ----

  const loadVehicleDoc = useCallback(async (vehicleId) => {
    if (!vehicleId) {
      setVehicleDoc(null);
      return;
    }

    setLoadingVehicle(true);
    try {
      // Ajusta aquí si tu colección real es otra.
      // Por tu UI anterior: "vehiculos"
      const ref1 = doc(collection(db, "vehiculos"), vehicleId);
      const snap1 = await getDoc(ref1);

      if (snap1.exists()) {
        setVehicleDoc({ id: snap1.id, ...snap1.data() });
        return;
      }

      // fallback opcional si alguna vez cambiaste nombre de colección
      const ref2 = doc(collection(db, "vehicles"), vehicleId);
      const snap2 = await getDoc(ref2);

      if (snap2.exists()) {
        setVehicleDoc({ id: snap2.id, ...snap2.data() });
        return;
      }

      setVehicleDoc({ _missing: true, id: vehicleId });
    } catch (e) {
      console.error("loadVehicleDoc error:", e);
      setVehicleDoc({ _error: true, id: vehicleId });
    } finally {
      setLoadingVehicle(false);
    }
  }, []);

  useEffect(() => {
    // ojo: vehicleID puede venir del form o del user
    const vId = form.vehicleID || user?.vehicleID || "";
    if (vId) loadVehicleDoc(vId);
    else setVehicleDoc(null);
  }, [form.vehicleID, user?.vehicleID, loadVehicleDoc]);

  // --------------------------------------------------
  // Stats
  // --------------------------------------------------
  const stats = useMemo(() => {
    const total = Array.isArray(driverOperationData)
      ? driverOperationData.length
      : 0;
    const delivered = Array.isArray(driverOperationData)
      ? driverOperationData.filter((i) => i.status === 8).length
      : 0;
    const pending = total - delivered;
    return { total, delivered, pending };
  }, [driverOperationData]);

  // --------------------------------------------------
  // Extra: load driver config + stores + shifts + docs
  // --------------------------------------------------
  const loadExtra = useCallback(async () => {
    if (!driverId) return;

    setLoadingExtra(true);
    setErrExtra(null);

    try {
      // 1) drivers/{id}
      const dRef = doc(collection(db, "drivers"), driverId);
      const dSnap = await getDoc(dRef);
      const dData = dSnap.exists() ? dSnap.data() : null;
      setDriverDoc(dData);

      // 2) tiendas (xpress_ebiexStores)
      // (si no existe, no rompe)
      try {
        const sSnap = await getDocs(collection(db, "xpress_ebiexStores"));
        setStores(sSnap.docs.map((x) => ({ ...x.data(), id: x.id })));
      } catch (_) {
        setStores([]);
      }

      // 3) turnos (xpress_ebiexShifts)
      try {
        const shSnap = await getDocs(collection(db, "xpress_ebiexShifts"));
        setShifts(shSnap.docs.map((x) => ({ id: x.id, ...x.data() })));
      } catch (_) {
        setShifts([]);
      }

      // 4) Intento de “vínculo companydrivers” si se puede inferir companyId/email
      //    (si tu driverDoc o user trae companyId)
      const inferredCompanyId =
        dData?.companyId || user?.companyId || user?.userCompanyID || null;
      const inferredEmail = normalizeEmail(dData?.email || user?.email);

      if (
        inferredCompanyId &&
        inferredEmail &&
        process.env.REACT_APP_COL_COMPANIES
      ) {
        const companyDriversCol = collection(
          db,
          process.env.REACT_APP_COL_COMPANIES,
          inferredCompanyId,
          "companydrivers"
        );

        // docId email lowercase (nuevo), pero buscamos también legacy con query
        let link = null;

        const direct = await getDoc(doc(companyDriversCol, inferredEmail));
        if (direct.exists()) link = direct.data();

        if (!link) {
          const q1 = query(
            companyDriversCol,
            where("email", "==", inferredEmail),
            limit(1)
          );
          const qSnap = await getDocs(q1);
          if (!qSnap.empty) link = qSnap.docs[0].data();
        }

        setCompanyLinkDoc(link);
      } else {
        setCompanyLinkDoc(null);
      }

      // 5) Inicializar form (merge de fuentes: driverDoc + user + companyLinkDoc)
      const merged = {
        active: dData?.active ?? user?.active ?? true,
        isblocked: dData?.isblocked ?? companyLinkDoc?.isblocked ?? false,
        requireTracking: dData?.requireTracking ?? false,
        hiddenListMenus: dData?.hiddenListMenus ?? false,
        webRidersEnabled:
          dData?.webRidersEnabled ?? dData?.webLoginEnabled ?? true,
        workStoreId: dData?.workStoreId ?? "",
        storeIds: Array.isArray(dData?.storeIds) ? dData.storeIds : [],
        shiftIds: Array.isArray(dData?.shiftIds) ? dData.shiftIds : [],
        vehicleID: dData?.vehicleID ?? user?.vehicleID ?? "",
        plate: dData?.plate ?? user?.plate ?? user?.patente ?? "",
      };

      setForm(merged);
    } catch (e) {
      console.error(e);
      setErrExtra("No se pudo cargar configuración extra del conductor.");
    } finally {
      setLoadingExtra(false);
    }
  }, [driverId, user]);

  useEffect(() => {
    if (driverId) loadExtra();
  }, [driverId, loadExtra]);

  // --------------------------------------------------
  // Stores permitidas (Opción A) para crear turnos
  // --------------------------------------------------
  const allowedStoreIdsSet = useMemo(() => {
    const ids = Array.isArray(form.storeIds) ? form.storeIds : [];
    return new Set(ids.filter(Boolean));
  }, [form.storeIds]);

  const allowedStores = useMemo(() => {
    // solo stores asignadas al conductor (storeIds)
    // + opcional: filtra inactivas
    return (stores || [])
      .filter((s) => allowedStoreIdsSet.has(s.id))
      .filter((s) => s?.active !== false);
  }, [stores, allowedStoreIdsSet]);

  useEffect(() => {
    console.log("form.storeIds =>", form.storeIds);
    console.log("allowedStoreOptions =>", allowedStoreOptions);
    console.log(
      "stores ids =>",
      (stores || []).map((s) => s.id)
    );
    console.log(
      "allowedStores =>",
      (allowedStores || []).map((s) => s.id)
    );
  }, [shiftModalOpen]);

  const allowedStoreOptions = useMemo(() => {
    const ids = Array.isArray(form.storeIds) ? form.storeIds : [];
    const byId = new Map((stores || []).map((s) => [s.id, s]));

    // Construye options SOLO con las storeIds del driver (en el mismo orden)
    return ids.map((id) => {
      const s = byId.get(id);
      return (
        s ?? {
          id,
          name: id, // fallback si la tienda no está cargada en stores
          radiusMeters: null,
        }
      );
    });
  }, [form.storeIds, stores]);

  // --------------------------------------------------
  // Save (drivers/{id})
  // --------------------------------------------------
  const saveDriverConfig = useCallback(async () => {
    if (!driverId) return;
    setSaving(true);

    try {
      const dRef = doc(collection(db, "drivers"), driverId);

      const patch = {
        active: !!form.active,
        isblocked: !!form.isblocked,
        requireTracking: !!form.requireTracking,
        hiddenListMenus: !!form.hiddenListMenus,
        webRidersEnabled: !!form.webRidersEnabled,
        workStoreId: form.workStoreId || "",
        storeIds: Array.isArray(form.storeIds) ? form.storeIds : [],
        shiftIds: Array.isArray(form.shiftIds) ? form.shiftIds : [],
        vehicleID: form.vehicleID || "",
        plate: form.plate || "",
        updatedAt: Date.now(),
      };

      await updateDoc(dRef, patch);

      setToast({
        open: true,
        msg: "Configuración guardada.",
        severity: "success",
      });
      await loadExtra(); // refresh
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        msg: "Error guardando configuración.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  }, [driverId, form, loadExtra]);

  const loadDriverShifts = useCallback(async () => {
    if (!driverId) return;

    try {
      // turnos del driver (sin orderBy para evitar índices al principio)
      const qRef = query(
        collection(db, "xpress_ebiexShifts"),
        where("driverId", "==", driverId),
        limit(500)
      );

      const snap = await getDocs(qRef);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      console.log(data);

      setDriverShifts(data);
    } catch (e) {
      console.error("loadDriverShifts error:", e);
    }
  }, [driverId]);

  const createShiftForDriver = useCallback(async () => {
    if (!driverId) return;

    const storeId = newShift.storeId;
    const date = newShift.date; // "YYYY-MM-DD"
    const startTime = newShift.startTime; // "HH:mm"
    const endTime = newShift.endTime;

    if (!storeId) {
      setToast({
        open: true,
        msg: "Selecciona una tienda.",
        severity: "warning",
      });
      return;
    }
    if (!date || !startTime || !endTime) {
      setToast({
        open: true,
        msg: "Completa fecha y horas.",
        severity: "warning",
      });
      return;
    }
    if (endTime <= startTime) {
      setToast({
        open: true,
        msg: "La hora fin debe ser mayor a inicio.",
        severity: "warning",
      });
      return;
    }

    setSaving(true);

    try {
      const batch = writeBatch(db);

      // 1) Crear shift doc (doc auto-id)
      const shiftsCol = collection(db, "xpress_ebiexShifts");
      const shiftRef = doc(shiftsCol); // genera ID sin escribir aún
      const shiftId = shiftRef.id;

      batch.set(shiftRef, {
        id: shiftId,
        driverId,
        storeId,
        date, // "YYYY-MM-DD"
        startTime,
        endTime,
        status: newShift.status || "SCHEDULED",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 2) drivers/{driverId}: storeIds + shiftIds
      const driverRef = doc(collection(db, "drivers"), driverId);
      batch.update(driverRef, {
        storeIds: arrayUnion(storeId),
        shiftIds: arrayUnion(shiftId),
        updatedAt: Date.now(),
      });

      // 3) stores/{storeId}: driverIds
      const storeRef = doc(collection(db, "xpress_ebiexStores"), storeId);
      batch.update(storeRef, {
        driverIds: arrayUnion(driverId),
        updatedAt: serverTimestamp(),
      });

      await batch.commit();

      setToast({
        open: true,
        msg: "Turno creado correctamente.",
        severity: "success",
      });
      setShiftModalOpen(false);

      // refresh
      await loadExtra();
      await loadDriverShifts();
    } catch (e) {
      console.error("createShiftForDriver error:", e);
      setToast({ open: true, msg: "Error creando turno.", severity: "error" });
    } finally {
      setSaving(false);
    }
  }, [driverId, newShift, loadExtra, loadDriverShifts, setToast]);

  const getPaymentInfo = (driver) => driver?.paymentInfo?.paymentInfo || {};
  const getPaymentUpdated = (driver) =>
    driver?.paymentInfo?.updatedItem || null;

  const getBillingInfo = (driver) => driver?.billingInfo?.billingInfo || {};
  const getBillingUpdated = (driver) =>
    driver?.billingInfo?.updatedItem || null;

  const friendlyScheme = (scheme) => {
    if (!scheme) return "—";
    if (scheme === "boleta") return "Boleta de honorarios";
    if (scheme === "empresa") return "Empresa / Facturación";
    return scheme;
  };
  // --------------------------------------------------
  // Operation modules
  // --------------------------------------------------
  const renderOperation = () => {
    switch (opModule) {
      case "HistorialDriver":
        return <Historial driverOperationData={driverOperationData} />;
      case "RendimientoDriver":
        return <Rendimiento driverOperationData={driverOperationData} />;
      case "TrayectosDriver":
        return (
          <Trayectos
            dailyTrips={dailyTrips}
            driverOperationData={driverOperationData}
          />
        );
      case "PagosDriver":
        return <Pagos driverOperationData={driverOperationData} />;
      default:
        return <Historial driverOperationData={driverOperationData} />;
    }
  };

  // --------------------------------------------------
  // Styles
  // --------------------------------------------------
  const cardSx = {
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  };

  // --------------------------------------------------
  // Guards
  // --------------------------------------------------
  if (!user || !driverOperationData) return <LinearProgress />;

  // --------------------------------------------------
  // Render blocks
  // --------------------------------------------------
  const Header = () => (
    <Box
      sx={{
        mb: 2,
        backgroundColor: "#fff",
        padding: 2,
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 900,
              letterSpacing: 0.2,
              color: "primary.main",
            }}
          >
            Configuración del conductor
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            Driver ID: <strong>{driverId}</strong>
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Chip size="small" label={`Bicci: ${user?.bicciNumber || "—"}`} />
          <Chip size="small" label={`Entregadas: ${stats.delivered}`} />
          <Chip size="small" label={`Pendientes: ${stats.pending}`} />
          <Tooltip title="Recargar">
            <IconButton onClick={loadExtra} disabled={loadingExtra}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );

  const ProfileCard = () => (
    <Card sx={cardSx}>
      <CardContent>
        <Stack spacing={2} alignItems="center">
          <Avatar
            src={user.avatar}
            sx={{
              width: 96,
              height: 96,
              border: "3px solid rgba(94,114,185,0.25)",
            }}
          />
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              {user.name || "—"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {user.dni || "—"} • {user.phone || "—"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              {user.email || "—"}
            </Typography>
            {user.Address ? (
              <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
                {user.Address}
              </Typography>
            ) : null}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            justifyContent="center"
          >
            <Chip label={`Total: ${stats.total}`} />
            <Chip label={`Entregadas: ${stats.delivered}`} />
            <Chip label={`Pendientes: ${stats.pending}`} />
          </Stack>

          {/* Estado general */}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            justifyContent="center"
          >
            <Chip
              color={form.active ? "success" : "default"}
              label={form.active ? "Activo" : "Inactivo"}
              variant={form.active ? "filled" : "outlined"}
            />
            <Chip
              color={form.isblocked ? "error" : "default"}
              label={form.isblocked ? "Bloqueado" : "No bloqueado"}
              variant={form.isblocked ? "filled" : "outlined"}
            />
            <Chip
              color={form.webRidersEnabled ? "primary" : "default"}
              label={form.webRidersEnabled ? "Web Riders ON" : "Web Riders OFF"}
              variant={form.webRidersEnabled ? "filled" : "outlined"}
            />
          </Stack>
        </Stack>
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Volver
        </Button>

        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={saveDriverConfig}
          disabled={true} //{saving}
        >
          Guardar cambios
        </Button>
      </CardActions>
    </Card>
  );

  const TabsTop = () => (
    <Card sx={{ ...cardSx, mb: 2 }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab value="overview" label="Resumen" />
        <Tab value="operation" label="Operación" />
        <Tab value="access" label="Accesos" />
        <Tab value="geo" label="Geo Control" />
        <Tab value="shifts" label="Turnos/Horarios" />
        <Tab value="vehicle" label="Vehículo/Patente" />
        <Tab value="docs" label="Documentación/Contratos" />
        {/* <Tab value="raw" label="RAW/Debug" /> */}
        {/* quitar raw  wn produccion */}
      </Tabs>
    </Card>
  );

  const OverviewPanel = () => (
    <Card sx={cardSx}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
          Resumen operativo
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSx, boxShadow: "none" }}>
              <CardContent>
                <Typography sx={{ fontWeight: 900 }}>Órdenes</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Total: {stats.total}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Entregadas: {stats.delivered}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Pendientes/No entregadas: {stats.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSx, boxShadow: "none" }}>
              <CardContent>
                <Typography sx={{ fontWeight: 900 }}>App & tracking</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  AppVersion:{" "}
                  {safeString(user?.appVersion || driverDoc?.appVersion || "—")}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Tracking requerido: {form.requireTracking ? "Sí" : "No"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Menús ocultos: {form.hiddenListMenus ? "Sí" : "No"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSx, boxShadow: "none" }}>
              <CardContent>
                <Typography sx={{ fontWeight: 900 }}>Geo Control</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  workStoreId: {form.workStoreId || "—"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  storeIds:{" "}
                  {Array.isArray(form.storeIds) ? form.storeIds.length : 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  shiftIds:{" "}
                  {Array.isArray(form.shiftIds) ? form.shiftIds.length : 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {loadingExtra ? (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
          </Box>
        ) : errExtra ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning">{errExtra}</Alert>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );

  const OperationPanel = () => (
    <Card sx={cardSx}>
      <CardContent>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Button
            variant={opModule === "HistorialDriver" ? "contained" : "outlined"}
            onClick={() => setOpModule("HistorialDriver")}
          >
            Historial
          </Button>
          <Button
            variant={
              opModule === "RendimientoDriver" ? "contained" : "outlined"
            }
            onClick={() => setOpModule("RendimientoDriver")}
          >
            Rendimiento
          </Button>
          <Button
            variant={opModule === "TrayectosDriver" ? "contained" : "outlined"}
            onClick={() => setOpModule("TrayectosDriver")}
          >
            Trayectos
          </Button>
          <Button
            variant={opModule === "PagosDriver" ? "contained" : "outlined"}
            onClick={() => setOpModule("PagosDriver")}
          >
            Pagos
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            onClick={() =>
              window.open(
                `https://bicci-7ed2f.firebaseapp.com/management/drivers/${driverId}/summary`,
                "_blank"
              )
            }
          >
            Administración externa
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Box>{renderOperation()}</Box>
      </CardContent>
    </Card>
  );

  const AccessPanel = () => (
    <Card sx={cardSx}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
          Accesos, seguridad y flags
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card sx={{ ...cardSx, boxShadow: "none" }}>
              <CardContent>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                  Estado de cuenta
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.active}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, active: e.target.checked }))
                      }
                    />
                  }
                  label="Conductor activo"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.isblocked}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, isblocked: e.target.checked }))
                      }
                    />
                  }
                  label="Bloqueado (operación / acceso)"
                />

                <Divider sx={{ my: 1.5 }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.webRidersEnabled}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          webRidersEnabled: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Login Web Riders habilitado"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ ...cardSx, boxShadow: "none" }}>
              <CardContent>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                  App / Tracking
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.requireTracking}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          requireTracking: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Require Tracking"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.hiddenListMenus}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          hiddenListMenus: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Ocultar menús (hiddenListMenus)"
                />

                <Divider sx={{ my: 1.5 }} />

                <Alert severity="info">
                  Reset de contraseña / acciones sensibles se agregan aquí (con
                  tu API admin). Lo dejamos en UI lista para conectar endpoint.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const GeoPanel = () => (
    <Card sx={cardSx}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
          Geo Control (tiendas y evaluación por radio)
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Tienda principal (workStoreId)</InputLabel>
              <Select
                label="Tienda principal (workStoreId)"
                value={form.workStoreId || ""}
                onChange={(e) =>
                  setForm((p) => ({ ...p, workStoreId: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>Sin tienda</em>
                </MenuItem>
                {stores.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name || s.id}{" "}
                    {s.radiusMeters ? `• ${s.radiusMeters}m` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* <Typography variant="body2" sx={{ mt: 1, opacity: 0.75 }}>
              Modo recomendado: <strong>radius</strong> usando{" "}
              <code>radiusMeters</code> en cada tienda.
            </Typography> */}
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Tiendas permitidas (storeIds)</InputLabel>
              <Select
                label="Tiendas permitidas (storeIds)"
                multiple
                value={Array.isArray(form.storeIds) ? form.storeIds : []}
                onChange={(e) =>
                  setForm((p) => ({ ...p, storeIds: e.target.value }))
                }
              >
                {stores.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name || s.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* <Typography variant="body2" sx={{ mt: 1, opacity: 0.75 }}>
              Tip: define <code>workStoreId</code> como tienda “base” y{" "}
              <code>storeIds</code> como tiendas habilitadas.
            </Typography> */}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const ShiftsPanel = () => {
    // shifts del día seleccionado
    const dayKey = selectedDay.format("YYYY-MM-DD");

    const shiftsForDay = useMemo(() => {
      return (driverShifts || [])
        .filter((s) => s.date === dayKey)
        .sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));
    }, [driverShifts, dayKey]);

    // “días con turnos” para mostrar punto en el calendario
    const shiftDaysSet = useMemo(() => {
      const set = new Set();
      (driverShifts || []).forEach((s) => {
        if (s?.date) set.add(s.date);
      });
      return set;
    }, [driverShifts]);

    const openNewShift = () => {
      setNewShift((p) => {
        const firstAllowed = allowedStoreOptions?.[0]?.id ?? "";

        const isAllowed =
          p.storeId && allowedStoreOptions.some((s) => s.id === p.storeId);

        return {
          ...p,
          date: selectedDay.format("YYYY-MM-DD"),
          storeId: isAllowed ? p.storeId : firstAllowed,
        };
      });

      setShiftModalOpen(true);
    };

    return (
      <Card sx={cardSx}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Turnos y horarios
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                Calendario de turnos del conductor y creación de nuevos turnos.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={loadDriverShifts}
                disabled={loadingExtra}
              >
                Recargar turnos
              </Button>
              <Button
                variant="contained"
                onClick={openNewShift}
                disabled={allowedStores.length === 0}
              >
                + Crear turno
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            {/* CALENDARIO */}
            <Grid item xs={12} md={5}>
              <Card sx={{ ...cardSx, boxShadow: "none" }}>
                <CardContent>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      value={selectedDay}
                      onChange={(v) => v && setSelectedDay(v)}
                      // Badge dot en días con turnos
                      slots={{
                        day: (props) => {
                          const dateStr = dayjs(props.day).format("YYYY-MM-DD");
                          const hasShift = shiftDaysSet.has(dateStr);

                          return (
                            <Badge
                              overlap="circular"
                              variant={hasShift ? "dot" : "standard"}
                            >
                              <PickersDay {...props} />
                            </Badge>
                          );
                        },
                      }}
                    />
                  </LocalizationProvider>

                  <Divider sx={{ my: 1.5 }} />

                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Día seleccionado: <strong>{dayKey}</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* AGENDA */}
            <Grid item xs={12} md={7}>
              <Card sx={{ ...cardSx, boxShadow: "none" }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 900, mb: 1 }}>
                    Turnos del día
                  </Typography>

                  {!driverShifts ? (
                    <LinearProgress />
                  ) : shiftsForDay.length === 0 ? (
                    <Alert severity="info">
                      No hay turnos para este día. Puedes crear uno.
                    </Alert>
                  ) : (
                    <Stack spacing={1}>
                      {shiftsForDay.map((s) => (
                        <Box
                          key={s.id}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: "1px solid rgba(0,0,0,0.06)",
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 900 }}>
                              {s.startTime}–{s.endTime} • {s.storeId}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.75 }}>
                              Status: {s.status || "—"} • ShiftID: {s.id}
                            </Typography>
                          </Box>

                          {/* placeholder acciones futuras: editar/cancelar */}
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Chip
                              size="small"
                              label={s.status || "SCHEDULED"}
                            />
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* <Alert severity="info">
                    Al crear un turno: se escribe en{" "}
                    <code>xpress_ebiexShifts</code>, se agrega la tienda en{" "}
                    <code>drivers/{driverId}.storeIds</code> y se agrega el
                    conductor en
                    <code>xpress_ebiexStores/storeId.driverIds</code>.
                  </Alert> */}
                  {allowedStores.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Este conductor no tiene tiendas asignadas en{" "}
                      <strong>Geo Control</strong>. Primero agrega tiendas en{" "}
                      <code>storeIds</code> (pestaña Geo Control) para poder
                      crear turnos.
                    </Alert>
                  )}
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    Tiendas disponibles: solo las asignadas al conductor en Geo
                    Control (storeIds).
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Dialog
            open={shiftModalOpen}
            onClose={() => setShiftModalOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ fontWeight: 900 }}>
              Crear nuevo turno
            </DialogTitle>

            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="store-label">Tienda</InputLabel>
                    <Select
                      labelId="store-label"
                      label="Tienda"
                      value={newShift.storeId}
                      onChange={(e) =>
                        setNewShift((p) => ({ ...p, storeId: e.target.value }))
                      }
                    >
                      {allowedStoreOptions.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name || s.id}{" "}
                          {s.radiusMeters ? `• ${s.radiusMeters}m` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha"
                    type="date"
                    value={newShift.date}
                    onChange={(e) =>
                      setNewShift((p) => ({ ...p, date: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hora inicio"
                    type="time"
                    value={newShift.startTime}
                    onChange={(e) =>
                      setNewShift((p) => ({ ...p, startTime: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hora fin"
                    type="time"
                    value={newShift.endTime}
                    onChange={(e) =>
                      setNewShift((p) => ({ ...p, endTime: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      label="Status"
                      value={newShift.status}
                      onChange={(e) =>
                        setNewShift((p) => ({ ...p, status: e.target.value }))
                      }
                    >
                      <MenuItem value="SCHEDULED">SCHEDULED</MenuItem>
                      <MenuItem value="STARTED">STARTED</MenuItem>
                      <MenuItem value="FINISHED">FINISHED</MenuItem>
                      <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                variant="outlined"
                onClick={() => setShiftModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={createShiftForDriver}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : null}
              >
                Crear turno
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    );
  };

  const VehiclePanel = () => {
    const vInfo = user?.vehicleInfo?.vehicle || null;
    const updatedSec = user?.vehicleInfo?.updatedItem?.seconds || null;

    const vehicleName =
      vehicleDoc?.name ||
      vehicleDoc?.nombre ||
      vehicleDoc?.title ||
      vInfo?.typeLabel ||
      "—";

    const vehicleId =
      form.vehicleID || user?.vehicleID || vehicleDoc?.id || "—";

    // Armamos una lista “humana” (sin seconds/nanoseconds)
    const rows = [
      {
        key: "vehiculoDoc.name",
        label: labelMap["vehiculoDoc.name"],
        value: vehicleDoc?.name || "—",
      },
      {
        key: "vehicleInfo.vehicle.typeLabel",
        label: labelMap["vehicleInfo.vehicle.typeLabel"],
        value: vInfo?.typeLabel || "—",
      },
      {
        key: "vehicleInfo.vehicle.ownership",
        label: labelMap["vehicleInfo.vehicle.ownership"],
        value: formatOwnership(vInfo?.ownership),
      },
      {
        key: "vehicleInfo.vehicle.type",
        label: labelMap["vehicleInfo.vehicle.type"],
        value: vInfo?.type ? String(vInfo.type) : "—",
      },
      {
        key: "vehicleInfo.vehicle.require",
        label: labelMap["vehicleInfo.vehicle.require"],
        value: formatBool(vInfo?.require),
      },
      {
        key: "vehicleInfo.vehicle.color",
        label: labelMap["vehicleInfo.vehicle.color"],
        value: vInfo?.color ? String(vInfo.color) : "—",
      },
      {
        key: "vehicleInfo.updatedItem",
        label: labelMap["vehicleInfo.updatedItem"],
        value: formatTimestampSeconds(updatedSec),
      },
      {
        key: "vehiculoDoc.id",
        label: labelMap["vehiculoDoc.id"],
        value: vehicleId,
      },
    ].filter((r) => r.value !== "—"); // opcional: ocultar vacíos

    return (
      <Card sx={cardSx}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
            Vehículo y patente
          </Typography>

          <Grid container spacing={2}>
            {/* Resumen */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "none",
                }}
              >
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                    Vehículo actual
                  </Typography>

                  {loadingVehicle ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">
                        Cargando vehículo…
                      </Typography>
                    </Stack>
                  ) : vehicleDoc?._missing ? (
                    <Alert severity="warning">
                      No se encontró en catálogo <code>vehiculos</code>.
                      <br />
                      ID: <strong>{vehicleId}</strong>
                    </Alert>
                  ) : vehicleDoc?._error ? (
                    <Alert severity="error">
                      Error cargando vehículo.
                      <br />
                      ID: <strong>{vehicleId}</strong>
                    </Alert>
                  ) : (
                    <Stack spacing={0.75}>
                      <Typography sx={{ fontWeight: 900 }}>
                        {vehicleName}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.75 }}>
                        ID: {vehicleId}
                      </Typography>

                      {/* Chips */}
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {vInfo?.typeLabel ? (
                          <Chip size="small" label={vInfo.typeLabel} />
                        ) : null}
                        {vInfo?.ownership ? (
                          <Chip
                            size="small"
                            label={`Propiedad: ${formatOwnership(
                              vInfo.ownership
                            )}`}
                          />
                        ) : null}
                        {typeof vInfo?.require === "boolean" ? (
                          <Chip
                            size="small"
                            color={vInfo.require ? "warning" : "default"}
                            label={
                              vInfo.require
                                ? "Requiere validación"
                                : "No requiere"
                            }
                          />
                        ) : null}
                      </Stack>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Patente */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Patente"
                value={form.plate}
                onChange={(e) =>
                  setForm((p) => ({ ...p, plate: e.target.value }))
                }
              />
            </Grid>
          </Grid>

          {/* Tabla de atributos con labels */}
          <Box sx={{ mt: 2 }}>
            <Card
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "none",
              }}
            >
              <CardContent>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                  Datos del vehículo (legible)
                </Typography>

                {rows.length === 0 ? (
                  <Alert severity="info">
                    No hay datos de vehículo para mostrar.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {rows.map((r) => (
                      <Box
                        key={r.key}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          {r.label || r.key}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {safeString(r.value)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* RAW opcional borrar en produccion*/}
          {/* <Box sx={{ mt: 2 }}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 800 }}>RAW (debug)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ opacity: 0.75, mb: 1 }}>
                  Para comparar con lo que llega desde Firestore.
                </Typography>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(
                    {
                      vehicleInfo: user?.vehicleInfo || null,
                      vehiculoDoc: vehicleDoc || null,
                    },
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>
          </Box> */}
        </CardContent>
      </Card>
    );
  };

  const DocsPanel = () => {
    // 1) “candidatos” legacy: documents/docs/documentos
    const docsCandidate =
      user?.documents ||
      user?.docs ||
      user?.documentos ||
      driverDoc?.documents ||
      driverDoc?.docs ||
      driverDoc?.documentos ||
      null;

    // 2) Construimos lista real con tu esquema actual (top-level + objetos)
    const docs = useMemo(() => {
      const out = [];

      // ---------------------------
      // Identidad
      // ---------------------------
      pushDoc(out, {
        section: "Identidad",
        label: "Cédula (frente)",
        url: user?.basicInfo?.dniFrontUrl || user?.dniFile,
      });
      pushDoc(out, {
        section: "Identidad",
        label: "Cédula (reverso)",
        url: user?.basicInfo?.dniBackUrl,
      });

      // ---------------------------
      // Licencia
      // ---------------------------
      pushDoc(out, {
        section: "Licencia",
        label: "Licencia (frente)",
        url: user?.driverLicenseInfo?.licenseFrontUrl || user?.driverLicence,
        meta: {
          expiresAt: user?.driverLicenseInfo?.driverLicense?.expiresAt,
          licenseClass: user?.driverLicenseInfo?.driverLicense?.licenseClass,
          licenseNumber: user?.driverLicenseInfo?.driverLicense?.licenseNumber,
        },
      });
      pushDoc(out, {
        section: "Licencia",
        label: "Licencia (reverso)",
        url: user?.driverLicenseInfo?.licenseBackUrl,
        meta: {
          expiresAt: user?.driverLicenseInfo?.driverLicense?.expiresAt,
        },
      });

      // ---------------------------
      // Antecedentes / Compliance
      // ---------------------------
      pushDoc(out, {
        section: "Certificado",
        label: "Certificado de antecedentes",
        url: user?.compliance?.certFileUrl || user?.antecedentesFile,
        meta: {
          certDate: user?.compliance?.compliance?.certDate,
          declarationOk: user?.compliance?.compliance?.declarationOk,
        },
      });

      // ---------------------------
      // Firma / Protocolos (si existen en documents)
      // ---------------------------
      pushDoc(out, {
        section: "Firma / Protocolos",
        label: "Firma - Documento (PDF)",
        url: user?.documents?.signatureDocumentPdfUrl,
      });
      pushDoc(out, {
        section: "Firma / Protocolos",
        label: "Firma - Protocolo (PDF)",
        url: user?.documents?.signatureProtocolPdfUrl,
      });

      // ---------------------------
      // Foto / Avatar
      // ---------------------------
      pushDoc(out, {
        section: "Perfil",
        label: "Avatar",
        url: user?.avatar || user?.contactPreferencesInfo?.avatarUrl,
      });

      // ---------------------------
      // (Opcional) Escaneo de docsCandidate (documents/docs/documentos)
      // ---------------------------
      if (docsCandidate) {
        const scanned = collectUrlDocsFromKV(docsCandidate, "documents");
        scanned.forEach((d) => out.push({ ...d, id: `${d.label}:${d.url}` }));
      }

      return dedupeDocs(out);
    }, [user, driverDoc, docsCandidate]);

    // 3) Agrupar por sección
    const docsBySection = useMemo(() => {
      const map = new Map();
      for (const d of docs) {
        if (!map.has(d.section)) map.set(d.section, []);
        map.get(d.section).push(d);
      }
      // orden básico
      const order = [
        "Identidad",
        "Licencia",
        "Compliance",
        "Firma / Protocolos",
        "Perfil",
        "Otros",
      ];
      const entries = Array.from(map.entries()).sort((a, b) => {
        const ia = order.indexOf(a[0]);
        const ib = order.indexOf(b[0]);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      });
      // orden dentro por label
      for (const [, arr] of entries) {
        arr.sort((x, y) => String(x.label).localeCompare(String(y.label)));
      }
      return entries; // [ [section, docs[]], ... ]
    }, [docs]);

    // 4) Acciones: ver / descargar
    const handleOpen = (url) =>
      window.open(url, "_blank", "noopener,noreferrer");

    const handleDownload = async (url, label) => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();

        const fileName = guessFileNameFromUrl(url, label || "documento");
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);

        setToast({
          open: true,
          msg: `Descarga iniciada: ${fileName}`,
          severity: "success",
        });
      } catch (e) {
        console.error("download error:", e);
        setToast({
          open: true,
          msg: "No se pudo descargar (revisa permisos/CORS).",
          severity: "error",
        });
      }
    };

    // 5) UI: chips de vencimiento
    const ExpiryChip = ({ expiresAt }) => {
      if (!expiresAt) return null;
      const d = dayjs(expiresAt);
      if (!d.isValid())
        return <Chip size="small" label={`Vence: ${expiresAt}`} />;

      const days = d.diff(dayjs(), "day");
      const color = days < 0 ? "error" : days <= 30 ? "warning" : "default";
      const label =
        days < 0
          ? `Vencido: ${d.format("YYYY-MM-DD")}`
          : `Vence: ${d.format("YYYY-MM-DD")} (${days}d)`;

      return <Chip size="small" color={color} label={label} />;
    };

    return (
      <Card sx={cardSx}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
            Documentación, contratos y evidencias
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Documentos desde el perfil (DNI, licencia, antecedentes, firmas,
            etc.)
          </Alert>
          {docs.length === 0 ? (
            <Alert severity="warning">
              No se detectaron URLs de documentos en el perfil actual.
            </Alert>
          ) : (
            <Stack spacing={1.25}>
              {docsBySection.map(([section, items]) => (
                <Accordion key={section}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ width: "100%" }}
                    >
                      <Typography sx={{ fontWeight: 900 }}>
                        {section}
                      </Typography>
                      <Chip size="small" label={`${items.length}`} />
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Stack spacing={1}>
                      {items.map((d) => (
                        <Box
                          key={d.id}
                          sx={{
                            p: 1.25,
                            border: "1px solid rgba(0,0,0,0.06)",
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 900 }}
                            >
                              {d.label}
                            </Typography>

                            {/* metadata */}
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              sx={{ mt: 0.5 }}
                            >
                              {d?.meta?.licenseClass ? (
                                <Chip
                                  size="small"
                                  label={`Clase: ${d.meta.licenseClass}`}
                                />
                              ) : null}
                              {d?.meta?.licenseNumber ? (
                                <Chip
                                  size="small"
                                  label={`N°: ${d.meta.licenseNumber}`}
                                />
                              ) : null}
                              {d?.meta?.certDate ? (
                                <Chip
                                  size="small"
                                  label={`Cert: ${d.meta.certDate}`}
                                />
                              ) : null}
                              <ExpiryChip expiresAt={d?.meta?.expiresAt} />
                            </Stack>

                            <Typography
                              variant="caption"
                              sx={{ opacity: 0.75, wordBreak: "break-all" }}
                            >
                              {d.url}
                            </Typography>
                          </Box>

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Tooltip title="Ver / Abrir">
                              <IconButton onClick={() => handleOpen(d.url)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Descargar">
                              <IconButton
                                onClick={() => handleDownload(d.url, d.label)}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>

                            <Button
                              size="small"
                              variant="outlined"
                              endIcon={<OpenInNewIcon />}
                              onClick={() => handleOpen(d.url)}
                            >
                              Abrir
                            </Button>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
          <BillingPaymentOnboardingAccordions />
        </CardContent>
      </Card>
    );
  };

  const RawPanel = () => (
    <Card sx={cardSx}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
          RAW / Debug
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Esto es útil para encontrar dónde vienen los campos y confirmar el
          esquema real.
        </Alert>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 800 }}>
              user (fetcherUserdriver)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              {JSON.stringify(user, null, 2)}
            </pre>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 800 }}>
              driverDoc (drivers/{driverId})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              {JSON.stringify(driverDoc, null, 2)}
            </pre>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 800 }}>
              companyLinkDoc (companydrivers)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              {JSON.stringify(companyLinkDoc, null, 2)}
            </pre>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );

  const formatUpdatedItem = (updatedItem) => {
    const sec = updatedItem?.seconds;
    if (!sec) return "—";
    // sec está en UNIX seconds
    return dayjs.unix(sec).format("YYYY-MM-DD HH:mm:ss");
  };

  const BillingPaymentOnboardingAccordions = () => {
    const d = driverDoc || user || {};

    const pay = getPaymentInfo(d);
    const payUpdated = getPaymentUpdated(d);

    const bill = getBillingInfo(d);
    const billUpdated = getBillingUpdated(d);

    // onboarding (si existe en tu doc)
    const statusInfo = d.statusInfo || {};
    const consentsInfo = d.consentsInfo || {};

    const health = computeOnboardingHealth(d);

    return (
      <>
        <Alert
          severity={health.isComplete ? "success" : "warning"}
          sx={{ mt: 2, mb: 1 }}
        >
          <Typography sx={{ fontWeight: 900 }}>
            {health.isComplete
              ? "Perfil completo ✅ (Banco + Facturación + Documentos + Consentimientos)"
              : `Perfil incompleto ⚠️ Faltan ${health.missing.length} ítems`}
          </Typography>

          {!health.isComplete ? (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {health.missing.slice(0, 8).map((m) => (
                <li key={m}>
                  <Typography variant="body2">{m}</Typography>
                </li>
              ))}
              {health.missing.length > 8 ? (
                <li>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    …y {health.missing.length - 8} más
                  </Typography>
                </li>
              ) : null}
            </Box>
          ) : null}
        </Alert>
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 800 }}>Banco / pagos</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ opacity: 0.75, mb: 1 }}>
              Actualizado: <strong>{formatUpdatedItem(payUpdated)}</strong>
            </Typography>

            <List dense>
              <ListItem disableGutters>
                <ListItemText
                  primary="Banco"
                  secondary={safeString(pay.bank)}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Tipo de cuenta"
                  secondary={safeString(pay.accountType)}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="N° de cuenta"
                  secondary={safeString(pay.accountNumber)}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Titular (RUT)"
                  secondary={safeString(pay.holderRut)}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Titular (Nombre)"
                  secondary={safeString(pay.holderName)}
                />
              </ListItem>
            </List>

            {!Object.keys(pay || {}).length ? (
              <Alert severity="info">Sin datos de pago cargados.</Alert>
            ) : null}
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 800 }}>Facturación</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ opacity: 0.75, mb: 1 }}>
              Actualizado: <strong>{formatUpdatedItem(billUpdated)}</strong>
            </Typography>

            <List dense>
              <ListItem disableGutters>
                <ListItemText
                  primary="Esquema tributario"
                  secondary={friendlyScheme(bill.scheme)}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Razón social / Nombre"
                  secondary={safeString(bill.companyName)}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="RUT"
                  secondary={safeString(bill.companyRut)}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Giro"
                  secondary={safeString(bill.giro)}
                />
              </ListItem>
            </List>

            {!Object.keys(bill || {}).length ? (
              <Alert severity="info">Sin datos de facturación cargados.</Alert>
            ) : null}
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 800 }}>
              Estado onboarding / verificación
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* <Alert severity="info" sx={{ mb: 2 }}>
              Este bloque es el mismo concepto de Web Riders (ReviewSubmitForm):
              estado + consentimientos + revisión.
            </Alert> */}

            <List dense>
              <ListItem disableGutters>
                <ListItemText
                  primary="Estado"
                  secondary={safeString(statusInfo.status || d.status)}
                />
              </ListItem>

              {statusInfo.reviewAt ? (
                <ListItem disableGutters>
                  <ListItemText
                    primary="Última revisión"
                    secondary={safeString(statusInfo.reviewAt)}
                  />
                </ListItem>
              ) : null}

              {/* consentimientos */}
              <ListItem disableGutters>
                <ListItemText
                  primary="Aceptó términos"
                  secondary={consentsInfo.terms ? "Sí" : "No"}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Aceptó privacidad"
                  secondary={consentsInfo.privacy ? "Sí" : "No"}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Contrato firmado"
                  secondary={consentsInfo.contractSigned ? "Sí" : "No"}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Mandato pagos firmado"
                  secondary={consentsInfo.paymentSigned ? "Sí" : "No"}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Protocolo firmado"
                  secondary={consentsInfo.protocolSigned ? "Sí" : "No"}
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>
      </>
    );
  };

  const MainPanel = () => {
    if (tab === "overview") return <OverviewPanel />;
    if (tab === "operation") return <OperationPanel />;
    if (tab === "access") return <AccessPanel />;
    if (tab === "geo") return <GeoPanel />;
    if (tab === "shifts") return <ShiftsPanel />;
    if (tab === "vehicle") return <VehiclePanel />;
    if (tab === "docs") return <DocsPanel />;
    if (tab === "raw") return <RawPanel />;
    // quitar raw en producccion
    return <OverviewPanel />;
  };

  return (
    <Box
      sx={{
        p: { xs: 1, md: 2 },
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#fff",
      }}
    >
      <Header />

      {/* Layout */}
      <Grid container spacing={2}>
        {/* SIDEBAR */}
        <Grid item xs={12} md={4} xl={3}>
          <ProfileCard />

          {loadingExtra ? (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
            </Box>
          ) : errExtra ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning">{errExtra}</Alert>
            </Box>
          ) : null}
        </Grid>

        {/* MAIN */}
        <Grid item xs={12} md={8} xl={9}>
          <TabsTop />
          <MainPanel />
        </Grid>
      </Grid>

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriversDetails;
